import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FileText, Truck, ListChecks, Receipt, Wallet, AlertTriangle, BellRing, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { STATUS_BADGE, formatCurrency } from "@/lib/constants";
import { KpiTile, ActivePoTable, DispatchTable, TasksTable, DashHeader, MomStrip } from "@/components/DashboardKit";

export default function AccountsDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notified, setNotified] = useState(() => new Set());
  const { user } = useAuth();

  useEffect(() => {
    let mounted = true;
    api.get("/dashboard/accounts")
      .then((r) => mounted && setData(r.data))
      .catch(() => toast.error("Failed to load accounts dashboard"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!data) return;
    const batch = [];
    for (const t of data.tasks || []) if (t.overdue) batch.push({ k: `t-${t.id}`, m: `Overdue task: ${t.task_title}` });
    for (const i of (data.invoices || []).slice(0, 20)) {
      if (i.payment_status === "Pending") batch.push({ k: `inv-${i.id}`, m: `Payment pending: ${i.invoice_number} · ${i.customer || "—"}` });
    }
    const fresh = batch.filter((b) => !notified.has(b.k)).slice(0, 5);
    if (fresh.length) {
      fresh.forEach((n) => toast.warning(n.m));
      setNotified((prev) => { const n = new Set(prev); fresh.forEach((f) => n.add(f.k)); return n; });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (loading) return <div className="p-6 space-y-4"><Skeleton className="h-10 w-80" /><div className="grid grid-cols-4 gap-3">{[1,2,3,4].map(i=><Skeleton key={i} className="h-24" />)}</div><Skeleton className="h-80" /></div>;
  if (!data) return null;
  const s = data.summary || {};

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="accounts-dashboard-page">
      <DashHeader
        eyebrow="Accounts Dashboard"
        title={`Hello ${user?.name?.split(" ")[0]} — your accounts cockpit`}
        hint="Pending invoices, payment & collection status, dispatch flow and your assigned accounting tasks."
        right={<div className="flex gap-2">
          {s.overdue_tasks > 0 && <Badge variant="outline" className="border-red-200 text-red-700 bg-red-50 gap-1"><AlertTriangle className="h-3 w-3" />{s.overdue_tasks} overdue</Badge>}
          {s.outstanding > 0 && <Badge variant="outline" className="border-amber-200 text-amber-700 bg-amber-50 gap-1"><BellRing className="h-3 w-3" />{formatCurrency(s.outstanding)} outstanding</Badge>}
        </div>}
      />

      <MomStrip items={data.mom} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-grid">
        <KpiTile label="Active POs" value={s.active_pos} icon={FileText} accent="blue" testid="kpi-active-pos" />
        <KpiTile label="Outstanding" value={formatCurrency(s.outstanding)} sub={`${formatCurrency(s.collected)} collected`} icon={Wallet} accent="rose" testid="kpi-outstanding" />
        <KpiTile label="Monthly Expenses" value={formatCurrency(s.monthly_expenses)} icon={Receipt} accent="amber" testid="kpi-monthly-exp" />
        <KpiTile label="My Pending Tasks" value={s.pending_tasks} sub={`${s.overdue_tasks} overdue · ${s.due_soon_tasks} due soon`} icon={ListChecks} accent="violet" testid="kpi-tasks" />
      </div>

      <Tabs defaultValue="pos" data-testid="accounts-tabs">
        <TabsList className="h-auto flex-wrap bg-slate-100 p-1">
          <TabsTrigger value="pos" data-testid="tab-active-pos"><FileText className="h-3.5 w-3.5 mr-1.5" /> Active PO</TabsTrigger>
          <TabsTrigger value="dispatch" data-testid="tab-dispatch"><Truck className="h-3.5 w-3.5 mr-1.5" /> Dispatch Status</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks"><ListChecks className="h-3.5 w-3.5 mr-1.5" /> Pending Task</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices"><Receipt className="h-3.5 w-3.5 mr-1.5" /> Invoices Generated</TabsTrigger>
        </TabsList>

        <TabsContent value="pos"><Card className="border-slate-200"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Active POs</div>
          <ActivePoTable rows={data.active_pos || []} />
        </CardContent></Card></TabsContent>

        <TabsContent value="dispatch"><Card className="border-slate-200"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Dispatch Pipeline</div>
          <DispatchTable rows={data.dispatches || []} />
        </CardContent></Card></TabsContent>

        <TabsContent value="tasks"><Card className="border-slate-200"><CardContent className="p-5">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Assigned Accounting Tasks</div>
            <div className="flex gap-2">
              <Badge variant="outline" className="border-red-200 text-red-700 bg-red-50">{s.overdue_tasks} overdue</Badge>
              <Badge variant="outline" className="border-amber-200 text-amber-700 bg-amber-50">{s.due_soon_tasks} due 7d</Badge>
            </div>
          </div>
          <TasksTable rows={data.tasks || []} />
        </CardContent></Card></TabsContent>

        <TabsContent value="invoices"><Card className="border-slate-200"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Invoices Generated · {data.invoices.length}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200"><tr>
                {["Invoice No.", "Date", "Customer / Buyer", "Amount", "Source", "Status", "Payment"].map((h) => (
                  <th key={h} className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-600">{h}</th>
                ))}
                <th className="py-2.5 px-3 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-600">Open</th>
              </tr></thead>
              <tbody>
                {data.invoices.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-slate-500">No invoices generated.</td></tr>}
                {data.invoices.map((i) => (
                  <tr key={`${i.source}-${i.id}`} className={`border-b border-slate-100 hover:bg-slate-50/60 ${i.payment_status === "Pending" ? "bg-amber-50/30" : ""}`} data-testid={`inv-row-${i.id}`}>
                    <td className="py-2.5 px-3 font-mono font-semibold">{i.invoice_number || "—"}</td>
                    <td className="py-2.5 px-3 text-slate-700">{i.invoice_date || "—"}</td>
                    <td className="py-2.5 px-3 text-slate-900">{i.customer || "—"}</td>
                    <td className="py-2.5 px-3 font-mono">{i.amount != null ? formatCurrency(i.amount) : "—"}</td>
                    <td className="py-2.5 px-3 text-slate-500 capitalize">{i.source.replace("_", " ")}</td>
                    <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(i.status || "")}>{i.status || "—"}</Badge></td>
                    <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(i.payment_status || "")}>{i.payment_status || "—"}</Badge></td>
                    <td className="py-2.5 px-3 text-right">
                      <Link to={`/m/${i.source}/${i.id}`} target="_blank" rel="noreferrer" className="text-blue-600 inline-flex items-center" data-testid={`inv-open-${i.id}`}>
                        Open <ArrowUpRight className="h-3.5 w-3.5 ml-0.5" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent></Card></TabsContent>
      </Tabs>
    </div>
  );
}
